import Decimal from 'decimal.js';
import {ComponenteKitPedido} from './ComponenteKitPedido';

/** Também aceita DTOs JSON, sem depender de protótipos do servidor. */
export function permiteMultiplaEscolha(campo: any): boolean {
  return campo?.tipo === 'multipla-escolha' || campo?.tipo === 'componente-kit';
}

export class RegrasDeComponenteKit {
  static grupos(produto: any): any[] {
    return (produto?.camposAdicionais || []).filter((g: any) => g.tipo === 'componente-kit');
  }

  static erro(grupo: any, codigo: string, mensagem: string): any {
    return {codigo, mensagem: `${grupo?.nome || 'Composição do kit'}: ${mensagem}`, adicionalId: grupo?.id};
  }

  static configuracao(grupo: any): any {
    const c = grupo?.configComponenteKit;
    const positivo = (v: any) => Number.isSafeInteger(v) && v > 0;
    const dinheiro = (v: any) => Number.isFinite(v) && v >= 0;
    const erro = () => this.erro(grupo, 'KIT_CONFIGURACAO_INVALIDA', 'verifique as quantidades, os limites e a cobrança.');
    if (!c || !['FIXA', 'FECHADA', 'ESCOLHIDA_PELO_CLIENTE'].includes(c.modalidadeQuantidade) ||
        !['INCLUIDO', 'POR_QUANTIDADE'].includes(c.cobranca) || !positivo(c.qtdeMinimaPorOpcao) ||
        (c.qtdeMaximaDeOpcoes != null && !positivo(c.qtdeMaximaDeOpcoes))) return erro();
    if (c.modalidadeQuantidade === 'FIXA' && (!positivo(c.qtdeFixa) || c.qtdeFixa < c.qtdeMinimaPorOpcao ||
        (c.cobranca === 'POR_QUANTIDADE' && !dinheiro(c.preco)))) return erro();
    if (c.modalidadeQuantidade === 'FECHADA') {
      const qs = c.quantidades || [];
      if (!qs.length || qs.some((q: any) => !q.descricao?.trim() || !positivo(q.qtdeUnidades) ||
          q.qtdeUnidades < c.qtdeMinimaPorOpcao || (c.cobranca === 'POR_QUANTIDADE' && !dinheiro(q.preco)))) return erro();
      const ids = qs.filter((q: any) => q.id != null).map((q: any) => q.id);
      if (new Set(ids).size !== ids.length) return erro();
    }
    if (c.modalidadeQuantidade === 'ESCOLHIDA_PELO_CLIENTE' && (!positivo(c.qtdeMinima) || !positivo(c.incremento) ||
        c.qtdeMinima < c.qtdeMinimaPorOpcao || (c.qtdeMaxima != null && (!positivo(c.qtdeMaxima) || c.qtdeMaxima < c.qtdeMinima)) ||
        (c.cobranca === 'POR_QUANTIDADE' && (!positivo(c.qtdeReferencia) || !dinheiro(c.precoReferencia))))) return erro();
    return null;
  }

  static selecao(grupo: any, componentes: any[]): any {
    return (componentes || []).find(c => c.adicionalId === grupo.id) ||
      (grupo.configComponenteKit?.modalidadeQuantidade === 'FIXA' ? {adicionalId: grupo.id, qtdeUnidades: grupo.configComponenteKit.qtdeFixa, quantidadeFechadaId: null} : null);
  }

  static unidades(grupo: any, selecao: any): any {
    const config = this.configuracao(grupo);
    if (config) return config;
    const c = grupo.configComponenteKit, n = selecao?.qtdeUnidades;
    if (!Number.isSafeInteger(n) || n < 1) return this.erro(grupo, 'KIT_UNIDADES_INVALIDAS', 'informe uma quantidade inteira e positiva.');
    if (c.modalidadeQuantidade === 'FECHADA') {
      const q = c.quantidades.find((q: any) => q.id === selecao.quantidadeFechadaId && q.disponivel !== false);
      if (!q || q.qtdeUnidades !== n) return this.erro(grupo, 'KIT_QUANTIDADE_OBRIGATORIA', 'escolha uma quantidade disponível deste grupo.');
    } else {
      if (selecao.quantidadeFechadaId != null) return this.erro(grupo, 'KIT_UNIDADES_INVALIDAS', 'esta modalidade não usa uma alternativa fechada.');
      if (c.modalidadeQuantidade === 'FIXA' && n !== c.qtdeFixa) return this.erro(grupo, 'KIT_UNIDADES_INVALIDAS', `complete ${c.qtdeFixa} unidades.`);
      if (c.modalidadeQuantidade === 'ESCOLHIDA_PELO_CLIENTE') {
        if (n < c.qtdeMinima) return this.erro(grupo, 'KIT_MINIMO_COMPRA', `o mínimo é ${c.qtdeMinima}.`);
        if (c.qtdeMaxima != null && n > c.qtdeMaxima) return this.erro(grupo, 'KIT_MAXIMO_COMPRA', `o máximo é ${c.qtdeMaxima}.`);
        if ((n - c.qtdeMinima) % c.incremento !== 0) return this.erro(grupo, 'KIT_INCREMENTO_COMPRA', `escolha de ${c.incremento} em ${c.incremento}, a partir de ${c.qtdeMinima}.`);
      }
    }
    return null;
  }

  static distribuicao(grupo: any, selecao: any, escolhas: any[]): any {
    const erro = this.unidades(grupo, selecao);
    if (erro) return erro;
    const c = grupo.configComponenteKit, ids = new Set();
    let soma = 0;
    for (const escolha of escolhas) {
      const o = (grupo.opcoesDisponiveis || []).find((o: any) => o.id === escolha.opcao?.id);
      if (!o || o.disponivel === false || o.esconder || o.oculta || ids.has(o.id))
        return this.erro(grupo, 'KIT_OPCAO_INDISPONIVEL', 'escolha opções disponíveis, sem duplicação.');
      ids.add(o.id);
      if (!Number.isSafeInteger(escolha.qtde) || escolha.qtde < Math.max(c.qtdeMinimaPorOpcao, o.qtdeMinima || 1))
        return this.erro(grupo, 'KIT_MINIMO_POR_OPCAO', `${o.nome} precisa de pelo menos ${Math.max(c.qtdeMinimaPorOpcao, o.qtdeMinima || 1)} unidades.`);
      if (o.qtdeMaxima > 0 && escolha.qtde > o.qtdeMaxima)
        return this.erro(grupo, 'KIT_MAXIMO_POR_OPCAO', `${o.nome} permite até ${o.qtdeMaxima} unidades.`);
      soma += escolha.qtde;
    }
    if (c.qtdeMaximaDeOpcoes != null && ids.size > c.qtdeMaximaDeOpcoes)
      return this.erro(grupo, 'KIT_MAXIMO_DE_OPCOES', `escolha até ${c.qtdeMaximaDeOpcoes} opções.`);
    if (soma !== selecao.qtdeUnidades) return this.erro(grupo, 'KIT_SOMA_DIFERENTE', `${soma < selecao.qtdeUnidades ? 'faltam' : 'sobram'} ${Math.abs(selecao.qtdeUnidades - soma)} unidades.`);
    return null;
  }

  static valideItem(item: any, persistido = false): any {
    const grupos = this.grupos(item.produto), estados = item.componentesKit || [];
    if (!Array.isArray(estados)) return this.erro(null, 'KIT_COMPONENTES_INVALIDOS', 'informe os componentes em uma lista.');
    const ids = estados.map(e => e?.adicionalId);
    if (new Set(ids).size !== ids.length || ids.some(id => !grupos.some((g: any) => g.id === id)))
      return this.erro(null, 'KIT_COMPONENTES_INVALIDOS', 'há grupos duplicados ou que não pertencem ao produto.');
    if (item.produto?.tipo !== 'kit' || !grupos.length) return this.erro(null, 'KIT_CONFIGURACAO_INVALIDA', 'cadastre ao menos um grupo de composição no kit.');
    const multiplos = item.produto.camposAdicionais.filter(permiteMultiplaEscolha);
    for (const g of grupos) {
      const chave = 'lista' + multiplos.indexOf(g);
      const lista = item.adicionais?.[chave] || {};
      const escolhas = persistido ? (item.adicionaisMultiplaEscolha?.[chave]?.opcoes || []) :
        Object.keys(lista).filter(k => k.startsWith('opcao_') && lista[k]?.selecionada).map(k => lista[k]);
      const erro = this.distribuicao(g, this.selecao(g, estados), escolhas);
      if (erro) return erro;
    }
    return null;
  }

  static contribuicao(grupo: any, selecao: any): Decimal {
    const c = grupo.configComponenteKit;
    if (!c || c.cobranca === 'INCLUIDO' || !selecao || this.unidades(grupo, selecao)) return new Decimal(0);
    if (c.modalidadeQuantidade === 'FIXA') return new Decimal(c.preco);
    if (c.modalidadeQuantidade === 'FECHADA') return new Decimal(c.quantidades.find((q: any) => q.id === selecao.quantidadeFechadaId).preco);
    return new Decimal(c.precoReferencia).mul(selecao.qtdeUnidades).div(c.qtdeReferencia);
  }

  static precoBase(produto: any, componentes: any[]): number {
    return this.grupos(produto).reduce((s, g) => s.add(this.contribuicao(g, this.selecao(g, componentes))),
      new Decimal(produto.novoPreco != null ? produto.novoPreco : produto.preco || 0)).toNumber();
  }

  static snapshot(produto: any, componentes: any[]): ComponenteKitPedido[] {
    return this.grupos(produto).map((g: any) => {
      const e = this.selecao(g, componentes);
      const q = g.configComponenteKit.quantidades?.find((q: any) => q.id === e.quantidadeFechadaId);
      return Object.assign(new ComponenteKitPedido(), {adicionalId: g.id, qtdeUnidades: e.qtdeUnidades,
        quantidadeFechadaId: e.quantidadeFechadaId ?? null, nome: g.nome,
        descricaoQuantidade: q?.descricao || null, valor: this.contribuicao(g, e).toNumber()});
    });
  }
}
